import React from 'react';
import {
  Grid,
  GridNoRecords,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import { useParams } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { ColumnMenu } from '../../../../components/columnMenu';
import { NoRecords } from '../../../../components/grid/no-records';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { useGetEventReportCompetencies } from '../../../../hooks/services/use-reports';
import { EventReportHeaderResponse } from '../../../../types/responses/event-report-response';
import { DownloadReportAssessmentCompetencySummaryAsync } from '../../../../services/event-reports';

const initialDataState: State = {
  sort: [{ field: 'competency', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

interface RatingCompetenciesReportTabProps {
  headerReport: EventReportHeaderResponse;
}

export const RatingCompetenciesReportTab: React.FC<
  RatingCompetenciesReportTabProps
> = ({ headerReport }) => {
  const { eventId, detailId } = useParams();

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const { data, isLoading } = useGetEventReportCompetencies({
    type: 'ratings-summary',
    eventId,
    playlistId: detailId,
    params: `${toDataSourceRequestString(dataState)}`
  });

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const ratingColumn = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {props.dataItem.averageRating.toFixed(1)}
      </td>
    );
  };

  const handleExport = async () => {
    if (!eventId || !detailId) return;

    try {
      await DownloadReportAssessmentCompetencySummaryAsync(
        eventId,
        detailId,
        `Competency Ratings Summary - ${headerReport.name}`
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div
      className={'pt-2'}
      style={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden'
      }}>
      <GridToolBar
        searchPlaceholder={'Search competency'}
        columnsToSearch={['competency', 'description']}
        exportRef={gridExportRef}
        showGridMode={false}
        showCardMode={false}
        showBulkExport={true}
        useCustomExportExcel
        handleCustomExportToExcel={handleExport}
        {...dataState}
        onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}
      />
      <ExcelExport
        data={data?.data ?? []}
        ref={gridExportRef}
        fileName={`competency-ratings-summary.xlsx`}>
        <Grid
          id={'grid-teams'}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          data={data}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            field={'averageRating'}
            title={'Average Rating'}
            width={170}
            columnMenu={ColumnMenu}
            cells={{ data: (props) => ratingColumn(props) }}
          />
          <Column
            field={'competency'}
            title={'Competency'}
            width={180}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'description'}
            title={'Description'}
            columnMenu={ColumnMenu}
          />

          <GridNoRecords>
            {isLoading ? (
              <div
                className={
                  'd-flex flex-column align-items-center gap-2 text-muted'
                }>
                <Loader type={'pulsing'} />
                <span>Loading data</span>
              </div>
            ) : (
              <NoRecords />
            )}
          </GridNoRecords>
        </Grid>
      </ExcelExport>
    </div>
  );
};
